/**
 * Правила подавления диплинковых пушей.
 *
 * Диплинк-пуш (app_url без web_url) подавляется в двух случаях:
 *
 * 1. `deeplink-on-desktop` — платформа «web» (десктоп). На десктопе нет нативного
 *    приложения, поэтому пуш, ведущий только в приложение, показывать смысла нет.
 *    Флаг `skipDeeplinkPushOnDesktop` включён по дефолту — на десктопе диплинк
 *    открыть нечем в принципе.
 *
 * 2. `no-app` — у аккаунта нет признака `deeplinkTarget` (отсутствие признака или false),
 *    то есть по данным аналитики (Flexo-таргет) он не пользуется нативным приложением.
 *    Флаг `skipDeeplinkPushWithoutTarget` выключен по дефолту — решение о его включении
 *    требует сверки таргета с реальностью.
 *
 * Рубильник, напротив, не может быть выключен во время проверки; отсутствие значения
 * при `skipDeeplinkPushWithoutTarget: true` рассматривается как `false`.
 *
 * Оба правила применяются только к пушам, которым некуда вести: есть app_url,
 * но нет web_url. Пуш с web_url не подавляется никогда — нативки нет, отработает
 * веб-ссылка.
 */
import type { SwPlatform } from '../platform';

export type SkipReason = 'deeplink-on-desktop' | 'no-app';

export interface GetSkipReasonInput {
    appUrl?: string;
    webUrl?: string;
    platform: SwPlatform;
    deeplinkTarget?: boolean;
    skipDeeplinkPushOnDesktop: boolean;
    skipDeeplinkPushWithoutTarget: boolean;
}

/**
 * Узнать, подавлять ли диплинковый пуш.
 *
 * Возвращает причину подавления для пуша, которому некуда вести (app_url без web_url).
 * Пуш подавляется по приоритету:
 * 1. Десктопное правило (`deeplink-on-desktop`)
 * 2. Таргетное правило (`no-app`)
 *
 * Если пуш не подавляется, возвращает `null`.
 */
/**
 * Платформы, для которых Flexo-таргет существует. Пока только iOS: PAM-13237 собран по активности
 * в iOS-приложении Облака, аналогичного таргета для Android нет.
 *
 * Гард обязателен, потому что отсутствие признака означает разное на разных платформах. На iOS
 * «признака нет» = аккаунт вне таргета, и это как раз повод подавить. На Android «признака нет» =
 * таргета не существует ни у кого, и подавление выкосило бы диплинковые пуши у всей платформы.
 * Различить эти два случая по значению фичи невозможно: Omicron-условие даёт «фича есть» либо
 * «фичи нет», а не «есть и false», поэтому дискриминатором остаётся платформа.
 *
 * Когда появится Android-таргет — добавить сюда 'android' одной строкой (CLOUDWEB-21080).
 */
const TARGETED_PLATFORMS: readonly SwPlatform[] = ['ios'];

export function getSkipReason(input: GetSkipReasonInput): SkipReason | null {
    const { appUrl, webUrl, platform, deeplinkTarget, skipDeeplinkPushOnDesktop, skipDeeplinkPushWithoutTarget } = input;

    // Пуш с web_url не подавляется никогда — нативки нет, отработает веб-ссылка.
    if (webUrl) {
        return null;
    }

    // Правила применяются только к пушам, которые могут быть диплинками.
    if (!appUrl) {
        return null;
    }

    // Десктопное правило приоритетнее таргетного.
    if (platform === 'web' && skipDeeplinkPushOnDesktop) {
        return 'deeplink-on-desktop';
    }

    // Таргетное правило: нет признака нативки, рубильник включён и для платформы таргет вообще
    // существует. Последнее — не перестраховка, см. TARGETED_PLATFORMS.
    if (!deeplinkTarget && skipDeeplinkPushWithoutTarget && TARGETED_PLATFORMS.includes(platform)) {
        return 'no-app';
    }

    return null;
}
